// Create an external call-center account (#1261). The role is fixed to
// `call_center_agent` server-side (the form never sends a roleId); the
// source selector offers only ENABLED sources and marks disabled ones as
// unsafe. The HMAC secret is never requested or rendered here.
import { useCallback, useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import {
  CreateCallCenterAccountInputSchema,
  type CreateCallCenterAccountInput,
} from '@magimp/shared';
import { useCreateCallCenterAccount, useDepartmentsList } from '@magimp/shared-hooks';
import {
  EditorModal,
  Button,
  Input,
  PhoneInput,
  Select,
  FormRow,
  useEditorGuard,
  type SelectOption,
} from '../../ui/index.js';
import { PasswordField } from '../../account/index.js';
import { useLeadSources } from '../../catalog/index.js';
import { useToastStore } from '../../stores/toast.js';
import { errorMessage } from '../../api/errors.js';
import { phoneError } from '../../shared/index.js';

export interface CallCenterAccountCreateModalProps {
  onClose: () => void;
}

const DEFAULTS: CreateCallCenterAccountInput = {
  email: '',
  name: '',
  phone: '',
  departmentId: '',
  password: '',
  active: true,
  sourceId: '',
};

export function CallCenterAccountCreateModal({ onClose }: CallCenterAccountCreateModalProps) {
  const { t } = useTranslation(['callCenterAdmin', 'users', 'common']);
  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<CreateCallCenterAccountInput>({
    resolver: zodResolver(CreateCallCenterAccountInputSchema),
    defaultValues: DEFAULTS,
  });
  const create = useCreateCallCenterAccount();
  const [dirty, setDirty] = useState(false);
  const computeDirty = useCallback((v: Partial<CreateCallCenterAccountInput>): boolean => {
    if ((v.email ?? '') !== DEFAULTS.email) return true;
    if ((v.name ?? '') !== DEFAULTS.name) return true;
    if ((v.departmentId ?? '') !== DEFAULTS.departmentId) return true;
    if ((v.sourceId ?? '') !== DEFAULTS.sourceId) return true;
    if ((v.password ?? '') !== DEFAULTS.password) return true;
    const phone = v.phone ?? '';
    if (phone !== '' && !(phone.startsWith('+') && phone.length <= 4)) return true;
    return false;
  }, []);
  useEffect(() => {
    setDirty(computeDirty(watch()));
    const sub = watch((v) => setDirty(computeDirty(v)));
    return () => sub.unsubscribe();
  }, [watch, computeDirty]);

  const departmentsQ = useDepartmentsList({ active: true });
  const departmentOptions: SelectOption[] = (departmentsQ.data ?? [])
    .slice()
    .sort((a, b) => a.name.localeCompare(b.name, 'ru'))
    .map((d) => ({ value: d.id, label: d.name }));
  // INVARIANT: only ENABLED, non-archived sources are bindable (the
  //   backend rejects others with 400). Disabled rows are filtered out so
  //   the selector cannot offer an unsafe binding.
  const sourcesQ = useLeadSources();
  const sourceOptions: SelectOption[] = (sourcesQ.data ?? [])
    .filter((s) => s.enabled && s.archivedAt === null)
    .map((s) => ({ value: s.id, label: s.displayName }));

  const show = useToastStore((s) => s.show);

  const onSubmit = (values: CreateCallCenterAccountInput) => {
    create.mutate(values, {
      onSuccess: () => {
        show({ tone: 'success', text: t('callCenterAdmin:toasts.created') });
        reset(DEFAULTS);
        setDirty(false);
        onClose();
      },
      onError: (err) => show({ tone: 'error', text: errorMessage(err) }),
    });
  };

  return (
    <EditorModal
      open={true}
      title={t('callCenterAdmin:create.title')}
      width={480}
      dirty={dirty}
      busy={create.isPending}
      onConfirmClose={onClose}
    >
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <FormRow label={t('callCenterAdmin:fields.email')} error={errors.email?.message}>
          <Controller
            control={control}
            name="email"
            render={({ field }) => (
              <Input
                value={field.value ?? ''}
                onChange={field.onChange}
                invalid={!!errors.email}
                type="email"
                icon="mail"
                autoComplete="off"
              />
            )}
          />
        </FormRow>
        <FormRow label={t('users:fields.name')} error={errors.name?.message}>
          <Controller
            control={control}
            name="name"
            render={({ field }) => (
              <Input value={field.value ?? ''} onChange={field.onChange} invalid={!!errors.name} />
            )}
          />
        </FormRow>
        <FormRow
          label={t('users:fields.phone')}
          error={phoneError(errors.phone?.message, t('users:errors.invalidPhone'))}
        >
          <Controller
            control={control}
            name="phone"
            render={({ field }) => (
              <PhoneInput
                value={field.value ?? ''}
                onChange={field.onChange}
                invalid={!!errors.phone}
                testId="cc-account-phone"
              />
            )}
          />
        </FormRow>
        <FormRow label={t('users:fields.department')} error={errors.departmentId?.message}>
          <Controller
            control={control}
            name="departmentId"
            render={({ field }) => (
              <Select
                value={field.value ?? ''}
                onChange={(v) => field.onChange(v)}
                options={departmentOptions}
                placeholder={t('users:placeholders.departmentSelect')}
              />
            )}
          />
        </FormRow>
        <FormRow label={t('callCenterAdmin:fields.source')} error={errors.sourceId?.message}>
          <Controller
            control={control}
            name="sourceId"
            render={({ field }) => (
              <Select
                value={field.value ?? ''}
                onChange={(v) => field.onChange(v)}
                options={sourceOptions}
                placeholder={t('callCenterAdmin:placeholders.sourceSelect')}
              />
            )}
          />
        </FormRow>
        <FormRow label={t('users:fields.password')} error={errors.password?.message}>
          <Controller
            control={control}
            name="password"
            render={({ field }) => (
              <PasswordField
                value={field.value ?? ''}
                onChange={field.onChange}
                invalid={!!errors.password}
                hint={t('users:password.requirement')}
                showGenerate
                showCopy
                autoComplete="new-password"
              />
            )}
          />
        </FormRow>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
          <CancelButton onClose={onClose} label={t('common:actions.cancel')} />
          <Button type="submit" kind="primary" disabled={create.isPending}>
            {t('common:actions.create')}
          </Button>
        </div>
      </form>
    </EditorModal>
  );
}

function CancelButton({ onClose, label }: { onClose: () => void; label: string }) {
  const { confirmDestructive } = useEditorGuard();
  return (
    <Button kind="ghost" onClick={() => confirmDestructive(onClose)}>
      {label}
    </Button>
  );
}
